import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../lib/AuthContext';
import { usePageMeta } from '../../lib/usePageMeta';
import type { Source, BulkExportRequest, SourcesResponse, BulkExportResponse } from '../types/api.types';
import Button from '../ui/Button';

interface TierLimit {
  name: string;
  maxRecords: number;
  estimatedTime: string;
}

const TIER_LIMITS: Record<string, TierLimit> = {
  free: { name: 'Free', maxRecords: 0, estimatedTime: 'N/A' },
  starter: { name: 'Starter', maxRecords: 1000, estimatedTime: '~2-5 min' },
  growth: { name: 'Growth', maxRecords: 5000, estimatedTime: '~10-30 min' },
  agency: { name: 'Agency', maxRecords: 25000, estimatedTime: '~1-2 hours' },
};

const API_URL = import.meta.env.VITE_API_URL || '/api';

const BulkExportPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageMeta({ title: 'Bulk Export', description: 'Export licensed professional data in bulk by state, profession, and filters. CSV and JSON formats available.', path: '/bulk-export' });
  // Sources Query
  const { data: sourcesData, isLoading: sourcesLoading } = useQuery({
    queryKey: ['sources'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/sources`);
      if (!res.ok) throw new Error('Failed to fetch sources');
      return res.json() as Promise<SourcesResponse>;
    }
  });

  // Create Export Mutation
  const createExportMutation = useMutation({
    mutationFn: async (params: BulkExportRequest) => {
      const res = await fetch(`${API_URL}/bulk-export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('progeodata_token') || ''}`
        },
        body: JSON.stringify(params),
      });
      if (res.status === 401) { navigate({ to: '/auth' }); throw new Error('Please sign in'); }
      if (!res.ok) {
        const err = await res.json() as { error?: string };
        throw new Error(err.error || 'Failed to create export');
      }
      return res.json() as Promise<BulkExportResponse>;
    },
    onSuccess: (data) => {
      if (data.success && data.job) {
        navigate({ to: '/bulk-status', search: { job_id: data.job.id } });
      }
    }
  });

  const userTier = user?.subscription_tier || 'free';
  const tierLimit = TIER_LIMITS[userTier] || TIER_LIMITS.free;

  // Form state
  const [state, setState] = useState('');
  const [profession, setProfession] = useState('');
  const [zipCodesText, setZipCodesText] = useState('');
  const [limit, setLimit] = useState(1000);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Available professions for selected state
  const [availableProfessions, setAvailableProfessions] = useState<string[]>([]);

  // Parse ZIP codes from textarea
  const zipCodes = zipCodesText
    .split(/[\s,;\n]+/)
    .map((z) => z.trim())
    .filter((z) => /^\d{5}$/.test(z));

  // Update professions when state changes
  useEffect(() => {
    if (state && sourcesData?.sources) {
      const selectedSource = sourcesData.sources.find(
        (s: Source) => s.state === state
      );
      setAvailableProfessions(selectedSource?.professions || []);
      if (profession && !selectedSource?.professions.includes(profession)) {
        setProfession('');
      }
    } else {
      setAvailableProfessions([]);
    }
  }, [state, sourcesData, profession]);

  // Calculate estimate
  const estimatedRecords = Math.min(zipCodes.length * 100, limit);
  const estimatedTimeMinutes = Math.ceil(estimatedRecords / 500);
  const estimatedTimeFormatted =
    estimatedTimeMinutes < 60
      ? `~${estimatedTimeMinutes} minutes`
      : `~${Math.ceil(estimatedTimeMinutes / 60)} hours`;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!state) {
      newErrors.state = 'Please select a state';
    }

    if (!profession) {
      newErrors.profession = 'Please select a profession';
    }

    if (zipCodes.length === 0) {
      newErrors.zipCodes = 'Please enter at least one valid ZIP code';
    }

    if (limit < 1) {
      newErrors.limit = 'Limit must be at least 1';
    }

    if (limit > tierLimit.maxRecords) {
      newErrors.limit = `Limit exceeds your tier maximum of ${tierLimit.maxRecords.toLocaleString()} records`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const request: BulkExportRequest = {
      state,
      profession,
      limit,
      tier: userTier,
    };

    // Use first ZIP code as base location
    if (zipCodes.length > 0) {
      request.zip = zipCodes[0];
    }

    createExportMutation.mutate(request);
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const zips = text
        .split(/[\s,;\n]+/)
        .map((z) => z.trim())
        .filter((z) => /^\d{5}$/.test(z));
      setZipCodesText(zips.join('\n'));
    };
    reader.readAsText(file);
  };

  // Check if user has access
  if (userTier === 'free') {
    return (
      <div className="min-h-screen bg-slate-900 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-sky-900/20 to-purple-900/20 border-2 border-sky-500 rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-sky-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">
              Bulk Export - Paid Feature
            </h2>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              Bulk export is available on Starter plans and above. Upgrade to export
              thousands of records at once with automated processing.
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate({ to: '/' })} // Assuming pricing is on home or separate page
            >
              View Pricing Plans
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 py-12">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://progeodata.com/" },
            { "@type": "ListItem", "position": 2, "name": "Bulk Export", "item": "https://progeodata.com/bulk-export" }
          ]
        }) }}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Bulk Export</h1>
          <p className="text-lg text-slate-400">
            Export large datasets of professional licensing data
          </p>
        </div>

        {/* Tier Info */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Current Plan</p>
              <p className="text-2xl font-bold text-sky-400">{tierLimit.name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-400">Max Records per Export</p>
              <p className="text-2xl font-bold text-slate-200">
                {tierLimit.maxRecords.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Export Form */}
        <form onSubmit={handleSubmit} className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* State Dropdown */}
            <div className="space-y-2">
              <label htmlFor="state" className="block text-sm font-medium text-slate-300">
                State <span className="text-red-500">*</span>
              </label>
              <select
                id="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                disabled={sourcesLoading || createExportMutation.isPending}
                className={`w-full px-4 py-3 bg-slate-900 border ${errors.state ? 'border-red-500' : 'border-slate-700'
                  } rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <option value="">Select state...</option>
                {sourcesData?.sources.map((source: Source) => (
                  <option key={source.state} value={source.state}>
                    {source.state_name} ({source.state})
                  </option>
                ))}
              </select>
              {errors.state && <p className="text-sm text-red-500">{errors.state}</p>}
            </div>

            {/* Profession Dropdown */}
            <div className="space-y-2">
              <label htmlFor="profession" className="block text-sm font-medium text-slate-300">
                Profession <span className="text-red-500">*</span>
              </label>
              <select
                id="profession"
                value={profession}
                onChange={(e) => setProfession(e.target.value)}
                disabled={!state || createExportMutation.isPending}
                className={`w-full px-4 py-3 bg-slate-900 border ${errors.profession ? 'border-red-500' : 'border-slate-700'
                  } rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <option value="">Select profession...</option>
                {availableProfessions.map((prof) => (
                  <option key={prof} value={prof}>
                    {prof.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </option>
                ))}
              </select>
              {errors.profession && <p className="text-sm text-red-500">{errors.profession}</p>}
            </div>
          </div>

          {/* ZIP Codes Input */}
          <div className="space-y-2">
            <label htmlFor="zipCodes" className="block text-sm font-medium text-slate-300">
              ZIP Codes <span className="text-red-500">*</span>
            </label>
            <textarea
              id="zipCodes"
              value={zipCodesText}
              onChange={(e) => setZipCodesText(e.target.value)}
              placeholder="Enter ZIP codes (comma or newline separated)&#10;Example: 10001, 10002, 10003"
              rows={4}
              disabled={createExportMutation.isPending}
              className={`w-full px-4 py-3 bg-slate-900 border ${errors.zipCodes ? 'border-red-500' : 'border-slate-700'
                } rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm`}
            />
            <div className="flex items-center justify-between">
              <div>
                {errors.zipCodes && <p className="text-sm text-red-500">{errors.zipCodes}</p>}
                <p className="text-sm text-slate-400">
                  {zipCodes.length} valid ZIP code{zipCodes.length !== 1 ? 's' : ''} entered
                </p>
              </div>
              <div>
                <label className="cursor-pointer text-sm text-sky-400 hover:text-sky-300 transition-colors">
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleCsvUpload}
                    className="hidden"
                  />
                  Upload CSV
                </label>
              </div>
            </div>
          </div>

          {/* Limit Input */}
          <div className="space-y-2">
            <label htmlFor="limit" className="block text-sm font-medium text-slate-300">
              Maximum Records
            </label>
            <input
              type="number"
              id="limit"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value) || 0)}
              min={1}
              max={tierLimit.maxRecords}
              disabled={createExportMutation.isPending}
              className={`w-full px-4 py-3 bg-slate-900 border ${errors.limit ? 'border-red-500' : 'border-slate-700'
                } rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed`}
            />
            {errors.limit && <p className="text-sm text-red-500">{errors.limit}</p>}
            <p className="text-sm text-slate-400">
              Your {tierLimit.name} plan allows up to {tierLimit.maxRecords.toLocaleString()} records per export
            </p>
          </div>

          {/* Estimate */}
          {zipCodes.length > 0 && (
            <div className="bg-sky-900/20 border border-sky-700 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <svg
                  className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-sky-400 mb-1">Export Estimate</p>
                  <p className="text-sm text-slate-300">
                    <strong>~{estimatedRecords.toLocaleString()}</strong> records • <strong>{estimatedTimeFormatted}</strong> processing time
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {createExportMutation.isError && (
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <svg
                  className="w-5 h-5 text-red-500 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-red-400">Export Error</p>
                  <p className="text-sm text-slate-300">{createExportMutation.error?.message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={createExportMutation.isPending || sourcesLoading}
              className="min-w-[200px]"
            >
              {createExportMutation.isPending ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Creating Export...
                </span>
              ) : (
                'Start Bulk Export'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkExportPage;
